/**
 * v96.5.0 · Manuales de Usuario (Centro de Documentación)
 * Lee los .md de src/docs/manuales (importados como texto) y los muestra con índice, buscador e impresión.
 * Formato: "# Título" del manual y secciones "## N. Título".
 */
import { useMemo, useState } from "react";
import { Search, Printer, BookOpen, ChevronRight } from "lucide-react";
import m01 from "../docs/manuales/01-acceso-y-navegacion.md";
import m02 from "../docs/manuales/02-ordenes-de-trabajo.md";
import m03 from "../docs/manuales/03-aprovisionamiento.md";
import m04 from "../docs/manuales/04-app-movil-tecnico.md";
import m05 from "../docs/manuales/05-configuracion-tipos-recetas-formularios.md";
import m06 from "../docs/manuales/06-agendamiento-horarios-eventos.md";
import m07 from "../docs/manuales/07-war-room.md";
import m08 from "../docs/manuales/08-administracion.md";

const RAW = [
  { key: "acceso", raw: m01, audience: "Todos los usuarios" },
  { key: "ots", raw: m02, audience: "Agendadores · Supervisores" },
  { key: "aprovisionamiento", raw: m03, audience: "Supervisores · NOC" },
  { key: "app", raw: m04, audience: "Técnicos · Contratistas" },
  { key: "configuracion", raw: m05, audience: "Administradores · Configuración" },
  { key: "agendamiento", raw: m06, audience: "Agendadores · Supervisores" },
  { key: "warroom", raw: m07, audience: "Supervisores · Gerencia" },
  { key: "admin", raw: m08, audience: "Administradores" },
];

function parse(raw) {
  const lines = String(raw || "").split("\n");
  let title = "";
  const sections = [];
  let cur = null;
  for (const line of lines) {
    const t = line.match(/^# (.+)$/);
    const s = line.match(/^## (\d+)\.\s+(.+)$/);
    if (t && !title) { title = t[1].trim(); continue; }
    if (s) { if (cur) sections.push(cur); cur = { num: +s[1], title: s[2].trim(), lines: [] }; continue; }
    if (cur) cur.lines.push(line);
  }
  if (cur) sections.push(cur);
  return { title, sections };
}

function inline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={i} className="px-1 rounded bg-gray-100 text-[0.85em]">{p.slice(1, -1)}</code>;
    return <span key={i}>{p}</span>;
  });
}

function renderBlock(lines) {
  const out = [];
  let i = 0, k = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (!l.trim()) { i++; continue; }
    if (/^### /.test(l)) { out.push(<h4 key={k++} className="font-bold text-[#0A3747] mt-4 mb-1">{inline(l.slice(4))}</h4>); i++; continue; }
    if (/^> /.test(l)) {
      const buf = [];
      while (i < lines.length && /^> /.test(lines[i])) { buf.push(lines[i].slice(2)); i++; }
      out.push(<div key={k++} className="my-3 px-3 py-2 rounded-lg border-l-4 border-amber-400 bg-amber-50 text-sm text-amber-900">{inline(buf.join(" "))}</div>);
      continue;
    }
    if (/^\|/.test(l)) {
      const rows = [];
      while (i < lines.length && /^\|/.test(lines[i])) { rows.push(lines[i]); i++; }
      const cells = (r) => r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = cells(rows[0]);
      const body = rows.slice(2).map(cells);
      out.push(
        <div key={k++} className="my-3 overflow-x-auto">
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-[#0A3747] text-white"><tr>{head.map((h, j) => <th key={j} className="text-left px-3 py-2 font-semibold">{inline(h)}</th>)}</tr></thead>
            <tbody>{body.map((r, ri) => <tr key={ri} className={ri % 2 ? "bg-gray-50" : ""}>{r.map((c, j) => <td key={j} className="px-3 py-2 border-t border-gray-100 align-top">{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^\d+\.\s/.test(l)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s/, "")); i++; }
      out.push(<ol key={k++} className="list-decimal pl-6 my-2 space-y-1 text-sm">{items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ol>);
      continue;
    }
    if (/^- /.test(l)) {
      const items = [];
      while (i < lines.length && /^- /.test(lines[i])) { items.push(lines[i].slice(2)); i++; }
      out.push(<ul key={k++} className="list-disc pl-6 my-2 space-y-1 text-sm">{items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ul>);
      continue;
    }
    const buf = [];
    while (i < lines.length && lines[i].trim() && !/^(### |> |\||\d+\.\s|- )/.test(lines[i])) { buf.push(lines[i]); i++; }
    out.push(<p key={k++} className="text-sm leading-relaxed my-2 text-gray-700">{inline(buf.join(" "))}</p>);
  }
  return out;
}

export function ManualesUsuarioContent() {
  const manuals = useMemo(() => RAW.map((m) => ({ ...m, ...parse(m.raw) })), []);
  const [sel, setSel] = useState(manuals[0].key);
  const [q, setQ] = useState("");
  const current = manuals.find((m) => m.key === sel) || manuals[0];
  const nq = q.trim().toLowerCase();
  const sections = nq
    ? current.sections.filter((s) => s.title.toLowerCase().includes(nq) || s.lines.some((l) => l.toLowerCase().includes(nq)))
    : current.sections;
  const hits = nq ? manuals.filter((m) => m.key !== sel && m.sections.some((s) => (s.title + s.lines.join(" ")).toLowerCase().includes(nq))) : [];
  const go = (num) => document.getElementById(`mu-${current.key}-${num}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="space-y-5" data-testid="manuales-usuario">
      <div className="rounded-2xl p-6 border border-[#E0E621]/30" style={{ background: "linear-gradient(135deg, #0A3747 0%, #135166 100%)" }}>
        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E0E621] mb-1">Manuales de usuario</div>
        <h2 className="text-2xl font-bold text-white">Guías por módulo</h2>
        <p className="text-white/80 text-sm mt-1">Elige un manual, usa el índice para ir a una sección o busca una palabra en todos los manuales.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 print:hidden">
        {manuals.map((m) => (
          <button key={m.key} onClick={() => setSel(m.key)} data-testid={`manual-${m.key}`}
            className={`text-left rounded-xl border px-3 py-2.5 transition ${sel === m.key ? "border-[#0A3747] bg-[#0A3747] text-white" : "border-gray-200 bg-white hover:border-[#0A3747]"}`}>
            <div className="flex items-center gap-2 text-sm font-bold"><BookOpen size={14} className={sel === m.key ? "text-[#E0E621]" : "text-[#0A3747]"} />{m.title}</div>
            <div className={`text-[11px] mt-0.5 ${sel === m.key ? "text-white/70" : "text-gray-500"}`}>{m.audience}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
        <aside className="lg:sticky lg:top-16 self-start space-y-3 print:hidden">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en los manuales…"
              className="w-full pl-8 pr-2 py-2 text-sm rounded-lg border border-gray-200" data-testid="manuales-search" />
          </div>
          <nav className="bg-white border border-gray-200 rounded-xl p-2">
            {current.sections.map((s) => (
              <button key={s.num} onClick={() => go(s.num)} className="w-full flex items-center gap-1 text-left px-2 py-1.5 text-[12px] rounded hover:bg-gray-50 text-gray-700">
                <ChevronRight size={12} className="text-gray-400" /> {s.num}. {s.title}
              </button>
            ))}
          </nav>
          {hits.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-2 text-[12px]">
              <div className="font-bold text-gray-500 px-2 mb-1">También en</div>
              {hits.map((m) => <button key={m.key} onClick={() => setSel(m.key)} className="block w-full text-left px-2 py-1 rounded hover:bg-gray-50 text-[#0A3747]">{m.title}</button>)}
            </div>
          )}
          <button onClick={() => window.print()} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50">
            <Printer size={14} /> Imprimir / PDF
          </button>
        </aside>

        <article className="bg-white border border-gray-200 rounded-2xl p-6" data-testid={`manual-content-${current.key}`}>
          <h1 className="text-2xl font-bold text-[#0A3747] mb-4">{current.title}</h1>
          {sections.length === 0 && <div className="text-sm text-gray-500">Sin resultados en este manual.</div>}
          {sections.map((s) => (
            <section key={s.num} id={`mu-${current.key}-${s.num}`} className="scroll-mt-20 mb-6">
              <h3 className="text-lg font-bold text-[#0A3747] border-b border-gray-100 pb-1 mb-2">{s.num}. {s.title}</h3>
              {renderBlock(s.lines)}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}

export default ManualesUsuarioContent;
